import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	fetchApplications,
	patchApplicationTargetStatus,
} from 'api/applications'
import { fetchUniversityById } from 'api/shortlister'
import { applicationsStore } from 'pages/ApplicationsPage/model/applicationsStore'
import { ROUTES } from 'shared/config/routes'
import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'
import { getHeaderImageFromGallery } from '../utils/galleryHeaderImage'
import { fetchPendingRecommendations } from 'api/dashboardRecommendations'
import { markApplicationSeenIfNew } from 'pages/ApplicationsPage/utils/markApplicationSeen'
import {
	syncStudentDocumentsSeen,
	syncStudentRecommendationsSeen,
} from 'shared/utils/counselorSeenSync'
import { chatWidgetStore } from 'shared/stores/chatWidgetStore'
import { buildKodaContext, stampKodaContext } from 'widgets/ChatWidget/kodaContext'
import { prioritySummaryStore } from 'shared/stores/prioritySummaryStore'
import { priorityTabsStore } from 'shared/stores/priorityTabsStore'
import { applicationDocumentsStore } from '../model/applicationDocumentsStore'
import {
	applicationStatusModifierClass,
	applicationStatusTitle,
	COUNSELOR_STATUS_OPTIONS,
	normalizeApplicationStatusKey,
} from '../utils/applicationStatusUi'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'

const EMPTY_REQUIREMENTS_DATA = {
	academic: [],
	tests: [],
	essays: [],
	recommendations: [],
	logoUrl: '',
	about: {
		tuition: '',
		livingExpenses: '',
		acceptanceRate: '',
		description: '',
	},
}

const toProgramName = major => {
	const value = String(major || '').trim()
	if (!value) return ''
	return `Program Name: ${value}`
}

const toList = value => {
	if (!value) return []
	if (Array.isArray(value)) {
		return value.map(item => String(item || '').trim()).filter(Boolean)
	}
	return String(value)
		.split(',')
		.map(item => item.trim())
		.filter(Boolean)
}

const toTestsList = examsAccepted => {
	const tests = toList(examsAccepted)
	return tests
}

const toAcademicList = profile => {
	const items = []
	if (profile?.twelfth_marks) items.push(`12th Grade: ${profile.twelfth_marks}`)
	if (profile?.graduation_marks)
		items.push(`Graduation Marks: ${profile.graduation_marks}`)
	if (profile?.work_experience)
		items.push(`Work Experience: ${profile.work_experience}`)
	return items
}

const normalizeUniversity = payload => {
	if (!payload) return null
	if (Array.isArray(payload)) return payload[0] || null
	return payload
}

const formatAddedDate = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	const day = String(date.getDate()).padStart(2, '0')
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const year = String(date.getFullYear()).slice(-2)
	return `${day}/${month}/${year}`
}

export const useApplicationDetailsPage = () => {
	const { applicationId } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const { showErrorSafe } = useSafeErrorBanner()
	const [activeTab, setActiveTab] = useState('requirements')
	const [universityProfile, setUniversityProfile] = useState(null)
	const [isUniversityLoading, setIsUniversityLoading] = useState(false)
	const [sendMessageOpen, setSendMessageOpen] = useState(false)
	const [updateStatusOpen, setUpdateStatusOpen] = useState(false)
	const [statusSaving, setStatusSaving] = useState(false)

	useEffect(() => {
		ensureStudentsLoaded().catch(() => {})
	}, [])

	useEffect(() => {
		let alive = true

		const loadApplications = async () => {
			if (applicationsStore.applications?.length) return

			try {
				const response = await fetchApplications()
				if (!alive) return
				const applications = response?.applications || []
				applicationsStore.setApplications(applications)
			} catch (error) {
				if (!alive) return
				console.warn('Failed to load applications for details page:', error)
			}
		}

		loadApplications()

		return () => {
			alive = false
		}
	}, [])

	const currentApplication = (() => {
		const normalizedId = String(applicationId || '').trim()
		if (!normalizedId) return null

		return (
			applicationsStore.applications.find(
				item => String(item?.id || '').trim() === normalizedId,
			) || null
		)
	})()

	const applicationTargetId = useMemo(
		() =>
			String(
				currentApplication?.applicationTargetId || currentApplication?.id || '',
			).trim(),
		[currentApplication],
	)

	useEffect(() => {
		if (!currentApplication?.isNewApplication) return
		void markApplicationSeenIfNew(currentApplication)
	}, [currentApplication])

	// The url identifies the application, not the student it belongs to; Koda needs both.
	// openEssayId is set by ApplicationDetailsPage while the essay review drawer is open.
	const [openEssayId, setOpenEssayId] = useState(null)

	useEffect(() => {
		const universityId =
			universityProfile?.university_id ||
			universityProfile?.universityId ||
			currentApplication?.universityId ||
			null
		// Backend essays route expects activeTab === 'essays' (plural).
		const kodaTab =
			openEssayId || activeTab === 'essay' || activeTab === 'essays'
				? 'essays'
				: activeTab
		chatWidgetStore.setPageContext(
			stampKodaContext(
				buildKodaContext({
					source: 'application_details',
					activeTab: kodaTab,
					subject: {
						studentId: currentApplication?.studentId,
						studentName: currentApplication?.studentName,
					},
					entities: {
						applicationTargetId,
						...(universityId ? { universityId } : {}),
						...(openEssayId ? { essayId: openEssayId } : {}),
					},
				}),
			),
		)
	}, [
		activeTab,
		applicationTargetId,
		currentApplication?.studentId,
		currentApplication?.studentName,
		currentApplication?.universityId,
		universityProfile,
		openEssayId,
	])

	useEffect(() => {
		const studentId = currentApplication?.studentId
		if (!studentId) return
		void applicationDocumentsStore.load(studentId)
	}, [currentApplication?.studentId])

	useEffect(() => {
		const studentId = String(currentApplication?.studentId || '').trim()
		if (!studentId) return
		const docsCount = Number(currentApplication?.newDocumentsCount) || 0
		if (docsCount > 0) {
			syncStudentDocumentsSeen(studentId, { fileCountForUi: docsCount })
		}
	}, [
		currentApplication?.studentId,
		currentApplication?.newDocumentsCount,
	])

	useEffect(() => {
		const studentId = String(currentApplication?.studentId || '').trim()
		const targetId = String(
			currentApplication?.applicationTargetId || currentApplication?.id || '',
		).trim()
		if (!studentId) return
		if ((Number(prioritySummaryStore.counts.recommendations) || 0) <= 0) {
			return
		}

		let cancelled = false

		const findPendingForApp = items =>
			(Array.isArray(items) ? items : []).find(item => {
				const sid = String(item?.studentId || '').trim()
				const tid = String(item?.applicationTargetId || '').trim()
				if (sid !== studentId) return false
				if (targetId && tid) return tid === targetId
				return true
			})

		const run = async () => {
			let pendingItem = findPendingForApp(
				priorityTabsStore.getTab('recommendations')?.items,
			)

			if (!pendingItem) {
				try {
					const res = await fetchPendingRecommendations()
					if (cancelled) return
					pendingItem = findPendingForApp(res?.data?.items)
				} catch (error) {
					console.error(
						'Failed to load pending recommendations for App Details:',
						error,
					)
					return
				}
			}

			if (cancelled || !pendingItem) return

			syncStudentRecommendationsSeen(studentId, {
				anchorAt: pendingItem.lastUpdatedAt || undefined,
				applicationTargetId:
					pendingItem.applicationTargetId || targetId || undefined,
				cardId: pendingItem.id,
				status: pendingItem.status,
			})
		}

		void run()

		return () => {
			cancelled = true
		}
	}, [currentApplication?.studentId, currentApplication?.applicationTargetId, currentApplication?.id])

	const applicationStatusKey = useMemo(
		() => normalizeApplicationStatusKey(currentApplication?.applicationStatus),
		[currentApplication?.applicationStatus],
	)

	const applicationStatusHeading = useMemo(
		() =>
			`Application ${applicationStatusTitle(currentApplication?.applicationStatus)}`,
		[currentApplication?.applicationStatus],
	)

	const applicationStatusClassMod = useMemo(
		() => applicationStatusModifierClass(currentApplication?.applicationStatus),
		[currentApplication?.applicationStatus],
	)

	const markSubmittedDisabled = applicationStatusKey === 'submitted'

	const sendMessageStudents = useMemo(() => {
		const sid = String(currentApplication?.studentId || '').trim()
		if (!sid) return []
		return [
			{
				id: sid,
				studentId: sid,
				name: currentApplication?.studentName || '',
			},
		]
	}, [currentApplication?.studentId, currentApplication?.studentName])

	const backContext = useMemo(() => {
		const backTo = location.state?.backTo || null

		if (backTo?.type === 'studentProfile' && backTo?.studentId) {
			return {
				label: 'Back',
				path: `${ROUTES.STUDENT_PROFILE}/${encodeURIComponent(
					String(backTo.studentId),
				)}`,
				state: { openTab: 'applications' },
			}
		}

		if (backTo?.type === 'dashboard') {
			return {
				label: 'Back',
				path: ROUTES.DASHBOARD,
				state: {
					priorityTab: backTo.priorityTab || null,
					scrollToPriority: true,
				},
			}
		}

		return { label: 'Back to Applications', path: ROUTES.APPLICATIONS, state: null }
	}, [location.state])

	const navigateBack = useCallback(() => {
		navigate(
			backContext.path,
			backContext.state ? { state: backContext.state } : undefined,
		)
	}, [navigate, backContext])

	const persistStatus = useCallback(
		async nextStatus => {
			const tid = applicationTargetId
			if (!tid) {
				showErrorSafe('Missing application id.')
				return
			}
			setStatusSaving(true)
			try {
				await patchApplicationTargetStatus(tid, nextStatus)
				applicationsStore.patchApplication(tid, {
					applicationStatus: normalizeApplicationStatusKey(nextStatus),
				})
			} catch (e) {
				showErrorSafe(
					e?.response?.data?.error || e?.message || 'Could not update status.',
				)
			} finally {
				setStatusSaving(false)
			}
		},
		[applicationTargetId, showErrorSafe],
	)

	const handleMarkSubmitted = useCallback(() => {
		if (markSubmittedDisabled || statusSaving) return
		persistStatus('submitted')
	}, [markSubmittedDisabled, persistStatus, statusSaving])

	const handlePickCounselorStatus = useCallback(
		value => {
			setUpdateStatusOpen(false)
			if (
				!value ||
				normalizeApplicationStatusKey(value) === applicationStatusKey
			)
				return
			persistStatus(value)
		},
		[applicationStatusKey, persistStatus],
	)

	const openSendMessage = useCallback(() => {
		setSendMessageOpen(true)
	}, [])

	const closeSendMessage = useCallback(() => {
		setSendMessageOpen(false)
	}, [])

	const toggleUpdateStatusMenu = useCallback(() => {
		setUpdateStatusOpen(v => !v)
	}, [])

	useEffect(() => {
		let alive = true
		const universityId = currentApplication?.universityId

		if (!universityId) {
			setUniversityProfile(null)
			setIsUniversityLoading(false)
			return () => {
				alive = false
			}
		}

		setIsUniversityLoading(true)
		setUniversityProfile(null)

		const loadUniversity = async () => {
			try {
				const response = await fetchUniversityById(universityId)
				if (!alive) return
				setUniversityProfile(normalizeUniversity(response))
			} catch (error) {
				if (!alive) return
				console.warn('Failed to load university profile:', error)
				setUniversityProfile(null)
			} finally {
				if (alive) setIsUniversityLoading(false)
			}
		}

		loadUniversity()

		return () => {
			alive = false
		}
	}, [currentApplication?.universityId])

	const tabs = useMemo(
		() => [
			{ id: 'requirements', label: 'Requirements' },
			{ id: 'checklist', label: 'Checklist' },
			{ id: 'essay', label: 'Essay' },
		],
		[],
	)

	const headerInfo = useMemo(() => {
		const universityName =
			universityProfile?.title || currentApplication?.university || ''
		return {
			studentName: currentApplication?.studentName,
			universityName,
			programName: toProgramName(currentApplication?.major),
			portal: '',
			location:
				universityProfile?.location || currentApplication?.country || '',
			intake: universityProfile?.intake_session || '',
			deadline:
				universityProfile?.application_deadline ||
				currentApplication?.deadline ||
				'',
			logoUrl: universityProfile?.logo_s3_link || null,
			gallery: universityProfile?.gallery || [],
			topBgUrl: getHeaderImageFromGallery(universityProfile?.gallery),
			addedAt: formatAddedDate(currentApplication?.addedAt),
		}
	}, [currentApplication, universityProfile])

	const requirementsData = useMemo(() => {
		if (!universityProfile) return EMPTY_REQUIREMENTS_DATA
		return {
			academic: toAcademicList(universityProfile),
			tests: toTestsList(universityProfile?.exams_accepted),
			essays: [],
			recommendations: [],
			logoUrl: universityProfile?.logo_s3_link || '',
			about: {
				tuition: universityProfile?.tution_fees || '',
				livingExpenses: universityProfile?.accomodation || '',
				acceptanceRate: universityProfile?.rank_country || '',
				description: universityProfile?.description || '',
			},
		}
	}, [universityProfile])

	return {
		tabs,
		activeTab,
		setActiveTab,
		headerInfo,
		requirementsData,
		isUniversityLoading,
		currentApplication,
		navigateBack,
		backLabel: backContext.label,
		applicationStatusHeading,
		applicationStatusClassMod,
		applicationStatusKey,
		markSubmittedDisabled,
		handleMarkSubmitted,
		statusSaving,
		sendMessageOpen,
		openSendMessage,
		closeSendMessage,
		sendMessageStudents,
		updateStatusOpen,
		setUpdateStatusOpen,
		toggleUpdateStatusMenu,
		handlePickCounselorStatus,
		counselorStatusOptions: COUNSELOR_STATUS_OPTIONS,
		setOpenEssayId,
	}
}
